import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, AxiosHeaders } from 'axios';
import appConfig, { isDemoHost } from '../config/api.config';
import AuthService from '../services/authService';
import AuthGuard from './authGuard';
import { logger } from './logger';

// Extend the config type to include retry property
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  retry?: boolean;
}

// Types
export interface ApiError {
  message: string;
  code?: string;
  status?: number;
  originalError?: any;
}

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: appConfig.API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor
apiClient.interceptors.request.use(
    async (config) => {
    // Skip auth check for auth endpoints
    if (config.url?.includes('/auth/')) {
      return config;
    }
    
    const token = AuthService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // If no token and not in dev mode with auth disabled, wait for auth
      const isAuthDisabled = appConfig.STAGE_NO_AUTH;
      if (!isAuthDisabled) {
        logger.warn('API request attempted without authentication token, waiting for auth...', 'APIClient');
        
        // Wait for authentication to complete
        const isAuthenticated = await AuthGuard.waitForAuth();
        
        if (isAuthenticated) {
          // Auth completed, get the token now
          const newToken = AuthService.getToken();
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
        } else {
          // Auth failed or not available
          return Promise.reject(new Error('Authentication required'));
        }
      }
    }
    
    return config;
  },
  (error) => {
    logger.error('Request error', 'APIClient', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Check for success field in response if API follows that pattern
    const data = response.data;
    if (data && typeof data.success === 'boolean' && !data.success) {
      // If the API indicates failure via a success flag
      return Promise.reject({
        message: data.message || 'خطای سرور',
        status: response.status,
        originalError: data
      });
    }
    return response;
  },
  async (error: AxiosError<any, ExtendedAxiosRequestConfig>) => {
    // Format error for consistent handling
    const apiError: ApiError = {
      message: 'خطای ارتباط با سرور',
      originalError: error
    };

    if (error.response) {
      // The request was made and the server responded with a status code
      // outside of the range of 2xx
      apiError.status = error.response.status;
      
      // Handle different error status codes
      switch (error.response.status) {
        case 400:
          apiError.message = 'درخواست نامعتبر';
          apiError.code = 'BAD_REQUEST';
          break;
        case 401:
          apiError.message = 'دسترسی غیرمجاز';
          apiError.code = 'UNAUTHORIZED';

          if (appConfig.STAGE_NO_AUTH) {
            apiError.message = 'درخواست بدون احراز هویت - حالت استیج';
            break;
          }
          
          const axiosConfig = error.config as ExtendedAxiosRequestConfig;
          
          // Try to refresh token if we haven't already
            if (!axiosConfig.retry && AuthService.getRefreshToken()) {
            axiosConfig.retry = true;
            
            try {
              logger.info('Access token expired, attempting to refresh...', 'APIClient');
              const tokens = await AuthService.refreshToken();
              
              // Retry the original request with new token
              if (tokens.access_token) {
                // Create proper AxiosHeaders instance
                const headers = new AxiosHeaders(axiosConfig.headers);
                headers.set('Authorization', `Bearer ${tokens.access_token}`);
                axiosConfig.headers = headers;
                return apiClient(axiosConfig);
              }
            } catch (refreshError) {
              logger.error('Token refresh failed', 'APIClient', refreshError);
              // Continue to clear auth and redirect
            }
          }
          
          // If refresh failed or no refresh token, redirect to Desk SSO
          logger.info('Authentication failed, redirecting...', 'APIClient');
          AuthService.clearAuth();
          AuthGuard.reset();
          if (!appConfig.STAGE_NO_AUTH) {
            if (isDemoHost()) {
              window.location.href = '/demo-login';
            } else {
              AuthService.redirectToLogin();
            }
          }
          break;
        case 403:
          apiError.message = 'دسترسی ممنوع';
          apiError.code = 'FORBIDDEN';
          break;
        case 404:
          apiError.message = 'داده‌ای یافت نشد';
          apiError.code = 'NOT_FOUND';
          break;
        case 500:
          apiError.message = 'خطای سرور داخلی';
          apiError.code = 'SERVER_ERROR';
          break;
        default:
          apiError.message = `خطا: ${error.response.status}`;
          apiError.code = 'HTTP_ERROR';
      }

      // Try to get more specific error message from response data
      const responseData = error.response.data as Record<string, any>;
      if (responseData?.message) {
        apiError.message = responseData.message;
      } else if (responseData?.detail) {
        apiError.message = responseData.detail;
      }
    } else if (error.request) {
      // The request was made but no response was received
      apiError.message = 'سرور پاسخی نداد';
      apiError.code = 'NO_RESPONSE';
    } else {
      // Something happened in setting up the request
      apiError.message = error.message || 'خطای غیرمنتظره';
      apiError.code = 'REQUEST_SETUP_ERROR';
    }

    // Log the error for debugging
    logger.error('API Error', 'APIClient', apiError);
    
    return Promise.reject(apiError);
  }
);

// Helper methods for common HTTP methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> => 
    apiClient.get<T, AxiosResponse<T>>(url, config)
      .then(response => response.data),
  
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.post<T, AxiosResponse<T>>(url, data, config)
      .then(response => response.data),
  
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.put<T, AxiosResponse<T>>(url, data, config)
      .then(response => response.data),
  
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    apiClient.delete<T, AxiosResponse<T>>(url, config)
      .then(response => response.data)
};

export default apiClient;
